import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { diaDoInstante } from "../../util/datas";
import { cx } from "./cx";

export type ProcessoResumo = {
  numero: string;
  numero_formatado: string;
  clientes: string[];
  fase: string | null;
  status: "ativo" | "suspenso" | "arquivado" | "encerrado";
  responsavel: string | null;
  atualizado_em: string;
};

const COLUNAS = "@2xl:grid-cols-[minmax(0,2fr)_13.5rem_minmax(0,1fr)_minmax(0,1fr)_5rem_3.5rem]";
const ROTULO_STATUS = { ativo: "", suspenso: "Suspenso", arquivado: "Arquivado", encerrado: "Encerrado" } as const;

/**
 * Lista geral de processos: uma linha de 36 px por processo quando há espaço, duas no celular. "Ativo" é o normal
 * e não aparece; só o que foge disso. Rola por dentro da tela; quem pagina é a tela (cursor da API, §2.5).
 */
export function ListaProcessos({ children }: { children: ReactNode }) {
  return (
    <div className="@container" role="table" aria-label="Processos">
      <div role="row" className={cx("hidden h-8 items-center gap-x-3 fio-b text-meta font-medium text-texto-3 @2xl:grid", COLUNAS)}>
        <span role="columnheader">Cliente</span>
        <span role="columnheader">Número</span>
        <span role="columnheader">Fase</span>
        <span role="columnheader">Responsável</span>
        <span role="columnheader">Situação</span>
        <span role="columnheader" className="text-right">Atividade</span>
      </div>
      <div role="rowgroup" className="entre-fios">{children}</div>
    </div>
  );
}

export function LinhaProcesso({ p }: { p: ProcessoResumo }) {
  const cliente = p.clientes.length === 0 ? "sem cliente" : p.clientes.length === 1 ? p.clientes[0] : `${p.clientes[0]} e mais ${p.clientes.length - 1}`;
  return (
    <div role="row" className={cx("group relative grid items-center gap-x-3 py-2 hover:bg-superficie-2",
      "grid-cols-[minmax(0,1fr)_auto]", COLUNAS)}>
      <span role="cell" className="min-w-0">
        <Link to="/processos/$numero" params={{ numero: p.numero }} title={p.clientes.join(", ")}
          className="block truncate font-medium text-texto-1 after:absolute after:inset-0 after:content-['']">
          {cliente}
        </Link>
      </span>
      <span role="cell" className="numeros col-start-1 truncate text-meta text-texto-3 @2xl:col-start-auto @2xl:text-corpo @2xl:text-texto-2">{p.numero_formatado}</span>
      <span role="cell" className="hidden truncate text-texto-2 @2xl:block">{p.fase ?? <span className="text-texto-3">sem fase</span>}</span>
      <span role="cell" className="hidden truncate text-texto-2 @2xl:block">{p.responsavel ?? <span className="text-texto-3">sem responsável</span>}</span>
      <span role="cell" className="col-start-2 row-start-1 text-meta text-texto-3 @2xl:col-start-auto @2xl:row-start-auto">{ROTULO_STATUS[p.status]}</span>
      <span role="cell" className="numeros col-start-2 row-start-2 text-right text-meta text-texto-3 @2xl:col-start-auto @2xl:row-start-auto">{diaDoInstante(p.atualizado_em)}</span>
    </div>
  );
}
